import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ALLOWED_URI_REGEXP, escapeHtml, sanitizeHtml } from 'app/core/utils';
import { Marked, Renderer, Tokens } from 'marked';

/**
 * Renders release-note Markdown to sanitised HTML.
 *
 * Parsing is `marked`, sanitising is `sanitizeHtml` — the two jobs are separated on purpose,
 * because `marked` makes no safety claim about its own output. The sanitiser was already in the
 * tree as a transitive dependency, so the parser is the only weight this adds.
 *
 * Two renderer overrides carry the feature's own rules:
 *
 * - Headings gain a `data-section` slug. It is what the stylesheet keys the section dot off, so the
 *   colour of the dot beside "New Features" never depends on matching rendered prose.
 * - Links outside the allowed protocols collapse to their label. Dropping the whole anchor rather
 *   than just its `href` keeps a dead link from looking clickable.
 *
 * `sanitizeHtml` enforces both an element whitelist and the same protocol rule independently, so a
 * mistake in either override cannot become a scripting bug on its own.
 */
const ALLOWED_TAGS: string[] = [
  'h1',
  'h2',
  'h3',
  'h4',
  'p',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'del',
  'code',
  'pre',
  'blockquote',
  'hr',
  'br',
  'a',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

const ALLOWED_ATTR: string[] = ['href', 'title', 'target', 'rel', 'data-section'];

const renderer: Renderer = new Renderer();

renderer.heading = function (token: Tokens.Heading): string {
  const content: string = this.parser.parseInline(token.tokens);

  // The slug is restricted to `[a-z0-9-]`, which is what makes it safe to interpolate here.
  return `<h${token.depth} data-section="${slugify(token.text)}">${content}</h${token.depth}>`;
};

renderer.link = function (token: Tokens.Link): string {
  const content: string = this.parser.parseInline(token.tokens);
  if (!ALLOWED_URI_REGEXP.test(token.href)) {
    return content;
  }

  return `<a href="${escapeHtml(token.href)}" target="_blank" rel="noopener noreferrer">${content}</a>`;
};

const markdownParser: Marked = new Marked({ gfm: true, breaks: false, renderer });

@Pipe({ name: 'markdown', standalone: true })
export class MarkdownPipe implements PipeTransform {
  private readonly _sanitizer = inject(DomSanitizer);

  transform(markdown: string | null | undefined): SafeHtml {
    const source: string = (markdown ?? '').trim();
    if (source === '') {
      return this._sanitizer.bypassSecurityTrustHtml('');
    }

    const html: string = markdownParser.parse(source, { async: false });

    // Angular's own sanitizer would strip `data-section`, so the trust boundary is `sanitizeHtml`.
    return this._sanitizer.bypassSecurityTrustHtml(sanitizeHtml(html, ALLOWED_TAGS, ALLOWED_ATTR));
  }
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
